import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import {
  AuditoriaFiltros,
  AuditoriaSap,
  OticSap,
  OticSapFiltros
} from '../../../interface/auditoria/auditoria-sap.interface';
import { AuditoriaSapService } from '../../../services/auditoria/auditoria-sap.service';
import { ViewDataComponent } from '../../modals/auditoria/view-data/view-data.component';

interface AuditoriaFiltrosForm {
  procNombre: FormControl<string | null>;
  centProcNombre: FormControl<string | null>;
  fechaDesde: FormControl<string | null>;
  fechaHasta: FormControl<string | null>;
}

type AuditoriaTab = 'centralizacion' | 'otic';

@Component({
  selector: 'app-list-auditoria',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    NgbDropdownModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule
  ],
  templateUrl: './list-auditoria.component.html',
  styleUrl: './list-auditoria.component.css'
})
export class ListAuditoriaComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly filtrosForm = new FormGroup<AuditoriaFiltrosForm>({
    procNombre: new FormControl<string | null>(null),
    centProcNombre: new FormControl<string | null>(null),
    fechaDesde: new FormControl<string | null>(null),
    fechaHasta: new FormControl<string | null>(null),
  });

  readonly displayedColumns: string[] = [
    'audId',
    'procNombre',
    'centProcNombre',
    'audFechaCreacion',
    'procesoOpciones'
  ];
  readonly oticDisplayedColumns: string[] = [
    'audId',
    'audFechaCreacion',
    'procesoOpciones'
  ];
  readonly pageSizeOptions: number[] = [5, 10, 25, 50];

  activeTab: AuditoriaTab = 'centralizacion';
  auditorias: AuditoriaSap[] = [];
  auditoriasOtic: OticSap[] = [];
  totalRegistros = 0;
  limit = 5;
  offset = 0;
  loading = false;

  constructor(
    private readonly auditoriaService: AuditoriaSapService,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly _modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.cargarAuditorias();
  }

  aplicarFiltros(): void {
    this.offset = 0;
    this.cargarTabActiva();
  }

  limpiarFiltros(): void {
    this.filtrosForm.reset({
      procNombre: null,
      centProcNombre: null,
      fechaDesde: null,
      fechaHasta: null
    });
    this.offset = 0;
    this.cargarTabActiva();
  }

  cambiarTab(tab: AuditoriaTab): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.offset = 0;
    this.cargarTabActiva();
  }

  cambiarPagina(event: PageEvent): void {
    if (event.pageSize !== this.limit) {
      this.limit = event.pageSize;
      this.offset = 0;
    } else {
      this.offset = event.pageIndex * this.limit;
    }

    this.cargarTabActiva();
  }

  visualizarJson(valor: string): string {
    const valorParseado = this.parseJsonSeguro(valor);
    return typeof valorParseado === 'string'
      ? valorParseado
      : JSON.stringify(valorParseado, null, 2);
  }

  truncar(valor: string, largoMaximo = 80): string {
    const valorVisual = this.visualizarJson(valor);
    return valorVisual.length > largoMaximo
      ? `${valorVisual.slice(0, largoMaximo)}…`
      : valorVisual;
  }

  private cargarAuditorias(): void {
    const filtros: AuditoriaFiltros = {
      limit: this.limit,
      offset: this.offset,
      procNombre:
        this.filtrosForm.controls.procNombre.value?.trim() || null,
      centProcNombre:
        this.filtrosForm.controls.centProcNombre.value?.trim() || null,
      fechaDesde: this.filtrosForm.controls.fechaDesde.value,
      fechaHasta: this.filtrosForm.controls.fechaHasta.value,
    };

    this.loading = true;
    this.spinner.show();

    this.auditoriaService.listarAuditoriaSap(filtros).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.loading = false;
        this.spinner.hide();
      })
    ).subscribe({
      next: (response) => {
        this.auditorias = response.auditoriaPaginados.rows;
        this.totalRegistros = response.auditoriaPaginados.count;
      },
      error: () => {
        this.auditorias = [];
        this.totalRegistros = 0;
        this.toastr.error(
          'No fue posible cargar las auditorías SAP.',
          'Error'
        );
      }
    });
  }

  private cargarAuditoriasOtic(): void {
    const filtros: OticSapFiltros = {
      limit: this.limit,
      offset: this.offset,
      fechaDesde: this.filtrosForm.controls.fechaDesde.value,
      fechaHasta: this.filtrosForm.controls.fechaHasta.value,
    };

    this.loading = true;
    this.spinner.show();

    this.auditoriaService.listarOticSap(filtros).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.loading = false;
        this.spinner.hide();
      })
    ).subscribe({
      next: (response) => {
        this.auditoriasOtic = response.oticSapPaginados.rows;
        this.totalRegistros = response.oticSapPaginados.count;
      },
      error: () => {
        this.auditoriasOtic = [];
        this.totalRegistros = 0;
        this.toastr.error(
          'No fue posible cargar las auditorías OTIC SAP.',
          'Error'
        );
      }
    });
  }

  private cargarTabActiva(): void {
    if (this.activeTab === 'otic') {
      this.cargarAuditoriasOtic();
      return;
    }

    this.cargarAuditorias();
  }

  private parseJsonSeguro(valor: string): unknown {
    try {
      return JSON.parse(valor);
    } catch {
      return valor;
    }
  }


  abrirModalVer(
    data: string,
    centProcNombre?: string,
    procNombre?: string
  ): void {
    const modalRef = this._modalService.open(ViewDataComponent, {
      backdrop: 'static',
       size: 'xl',
       windowClass: 'modal-payload'
    });
    modalRef.componentInstance.titulo = `Proceso “${procNombre ?? '-'}”`;
    modalRef.componentInstance.texto = `Centro “${centProcNombre ?? '-'}”`;
    modalRef.componentInstance.data = this.parseJsonSeguro(data);
  }

  abrirModalOtic(data: string, audId: number, tipo: string): void {
    const modalRef = this._modalService.open(ViewDataComponent, {
      backdrop: 'static',
      size: 'xl',
      windowClass: 'modal-payload'
    });
    modalRef.componentInstance.titulo = `Auditoría OTIC #${audId}`;
    modalRef.componentInstance.texto = tipo;
    modalRef.componentInstance.data = this.parseJsonSeguro(data);
  }
}
